import { BALL_CSS } from '@shootout-comps/core';
import type { Match, LiveMatchState, Player, BallColour, FrameResult } from '@shootout-comps/types';
import type { Translations } from '@shootout-comps/i18n';

// ─── Frame progress (best-of-N matches — standard/snooker900 only) ────────────────────────

export function FrameProgressBar({ bestOf, framesToWin, frameWins1, frameWins2, currentFrame, complete, p1, p2 }: {
  bestOf: number; framesToWin: number; frameWins1: number; frameWins2: number; currentFrame: number;
  complete: boolean; p1: Player; p2: Player;
}) {
  if (bestOf <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem', fontSize: '0.68rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      <span>{complete ? 'Final' : `Frame ${currentFrame} of ${bestOf}`}</span>
      <span style={{ color: 'var(--gold)', fontWeight: 700, fontFamily: 'Bebas Neue', fontSize: '1rem', letterSpacing: '0.04em', textTransform: 'none' }}>
        {p1.name.split(' ')[0]} {frameWins1} – {frameWins2} {p2.name.split(' ')[0]}
      </span>
      <span>(first to {framesToWin})</span>
    </div>
  );
}

export function FrameBreakdownList({ frames, p1, p2 }: { frames: FrameResult[]; p1: Player; p2: Player }) {
  if (frames.length === 0) return null;
  return (
    <div style={{ marginTop: '1.25rem', textAlign: 'left', maxWidth: '360px', marginLeft: 'auto', marginRight: 'auto' }}>
      <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', textAlign: 'center' }}>
        Frame-by-Frame
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {frames.map(f => (
          <div key={f.frameNumber} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', padding: '0.45rem 0.7rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ color: 'var(--chalk-dim)', flexShrink: 0 }}>Frame {f.frameNumber}</span>
            <span style={{ fontWeight: 600, fontFamily: 'Bebas Neue', fontSize: '0.95rem' }}>{f.score1} – {f.score2}</span>
            <span style={{ color: 'var(--gold)', fontSize: '0.7rem', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {(f.winnerId === String(p1._id) ? p1.name : p2.name).split(' ')[0]} won
            </span>
            {(f.highBreak1 > 0 || f.highBreak2 > 0) && (
              <span style={{ color: 'var(--chalk-dim)', fontSize: '0.65rem', flexShrink: 0 }}>
                HB {Math.max(f.highBreak1, f.highBreak2)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Extracted from MatchPlayPage.tsx — originally built for the "another tab is scoring this
// match" spectator mode, reused as-is for spectating a public tournament's match, since the
// read-only presentation is identical either way. MatchPlayPage.tsx imports Av/PH/SpectatorView
// from here rather than defining its own copies.

export const PH = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="32" fill="%23236038"/%3E%3Ccircle cx="32" cy="24" r="10" fill="%23c9a84c" opacity="0.7"/%3E%3Cellipse cx="32" cy="52" rx="16" ry="10" fill="%23c9a84c" opacity="0.7"/%3E%3C/svg%3E';

export function Av({ player, size = 64, active = false }: { player: Player; size?: number; active?: boolean }) {
  return (
    <div style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: `2px solid ${active ? '#2ecc71' : 'rgba(201,168,76,0.35)'}`, boxShadow: active ? '0 0 10px rgba(46,204,113,0.4)' : 'none', transition: 'all 0.3s' }}>
      <img src={player.profilePictureUrl ? player.profilePictureUrl : PH} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </div>
  );
}

export function SpectatorView({ match, ls, p1, p2, curPlayer, inProgress, breakOffPending, complete, remaining, clockPaused, shotLeft, shotWarn, shotOver, displayElapsed: _displayElapsed, matchDuration: _matchDuration, halfTime, inSecondHalf, shotLimit, fmt, widescreen, isStandard, p1Break, p2Break, p1HighBreak, p2HighBreak, p1AvgShotTime, p2AvgShotTime, p1MatchHighBreak, p2MatchHighBreak, bestOf, framesToWin, frameWins1, frameWins2, currentFrame, frames, t }: {
  match: Match;
  ls: LiveMatchState | undefined;
  p1: Player; p2: Player;
  curPlayer: Player | null;
  inProgress: boolean;
  breakOffPending: boolean;
  complete: boolean;
  remaining: number;
  clockPaused: boolean;
  shotLeft: number;
  shotWarn: boolean;
  shotOver: boolean;
  displayElapsed: number;
  matchDuration: number;
  halfTime: number;
  inSecondHalf: boolean;
  shotLimit: number;
  p1Break: number;
  p2Break: number;
  p1HighBreak: number;
  p2HighBreak: number;
  p1AvgShotTime: number;
  p2AvgShotTime: number;
  p1MatchHighBreak: number;
  p2MatchHighBreak: number;
  bestOf: number;
  framesToWin: number;
  frameWins1: number;
  frameWins2: number;
  currentFrame: number;
  frames: FrameResult[];
  fmt: string;
  widescreen: boolean;
  isStandard: boolean;
  t: Translations;
}) {
  const matchMins   = Math.floor(remaining / 60);
  const matchSecs   = remaining % 60;
  const timeFmt     = `${matchMins}:${String(matchSecs).padStart(2, '0')}`;
  const winner      = match.winner as Player | null;
  const isSnooker900 = fmt === 'snooker900';

  // Widescreen: single full-width column — larger score card + wider shot history below
  if (widescreen && inProgress) {
    return (
      <div style={{ padding: '0.5rem 0', display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'relative' }}>

        {/* ── Match Paused overlay ── */}
        {clockPaused && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
            borderRadius: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>⏸</div>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: '2.5rem', color: 'var(--chalk)', letterSpacing: '0.08em', lineHeight: 1 }}>
                Match Paused
              </div>
            </div>
          </div>
        )}

        {/* ── Score card — full width ── */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px',
          padding: '2rem 3rem',
        }}>
          <FrameProgressBar bestOf={bestOf} framesToWin={framesToWin} frameWins1={frameWins1} frameWins2={frameWins2} currentFrame={currentFrame} complete={complete} p1={p1} p2={p2} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Player 1 */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <Av player={p1} size={130} active={!breakOffPending && curPlayer?._id === p1._id} />
              <div style={{ fontWeight: 700, fontSize: '1.25rem', marginTop: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p1.name}</div>
              {!breakOffPending && curPlayer?._id === p1._id && (
                <div style={{ fontSize: '0.8rem', color: '#2ecc71', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.2rem' }}>▶ {t.atTheTable}</div>
              )}
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(6rem,15vw,10rem)', lineHeight: 1, color: !breakOffPending && curPlayer?._id === p1._id ? 'var(--gold)' : 'var(--chalk)', marginTop: '0.25rem' }}>
                {ls?.score1 ?? 0}
              </div>
              {p1Break > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--gold)', fontFamily: 'Bebas Neue', letterSpacing: '0.06em', marginTop: '0.15rem' }}>
                  Break: {p1Break}
                </div>
              )}
              {p1HighBreak > 0 && (
                <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.15rem' }}>
                  High break: {p1HighBreak}
                </div>
              )}
              {p1AvgShotTime > 0 && (
                <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>
                  Avg shot: {Math.round(p1AvgShotTime)}s
                </div>
              )}
            </div>

            {/* Centre column */}
            <div style={{ textAlign: 'center', flexShrink: 0, minWidth: '120px' }}>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: '2.5rem', color: 'var(--chalk-dim)' }}>{t.vsLabel}</div>
              {!isStandard && !clockPaused && (
                <div style={{
                  fontFamily: 'Bebas Neue', fontSize: 'clamp(2rem,5vw,3rem)',
                  color: remaining <= 60 ? '#e74c3c' : remaining <= 120 ? '#e67e22' : 'var(--chalk-dim)',
                  letterSpacing: '0.04em',
                }}>{timeFmt}</div>
              )}
              {!isStandard && clockPaused && <div style={{ fontSize: '1.5rem', color: 'var(--chalk-dim)' }}>⏸</div>}
              {!isStandard && halfTime > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '0.3rem' }}>
                  {inSecondHalf ? t.secondHalf : t.firstHalf}
                </div>
              )}
              {ls && ls.reds_remaining > 0 && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.85rem', color: 'var(--chalk-dim)' }}>
                  {ls.reds_remaining} {t.redsRemaining}
                </div>
              )}
            </div>

            {/* Player 2 */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <Av player={p2} size={130} active={!breakOffPending && curPlayer?._id === p2._id} />
              <div style={{ fontWeight: 700, fontSize: '1.25rem', marginTop: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p2.name}</div>
              {!breakOffPending && curPlayer?._id === p2._id && (
                <div style={{ fontSize: '0.8rem', color: '#2ecc71', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.2rem' }}>▶ {t.atTheTable}</div>
              )}
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(6rem,15vw,10rem)', lineHeight: 1, color: !breakOffPending && curPlayer?._id === p2._id ? 'var(--gold)' : 'var(--chalk)', marginTop: '0.25rem' }}>
                {ls?.score2 ?? 0}
              </div>
              {p2Break > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--gold)', fontFamily: 'Bebas Neue', letterSpacing: '0.06em', marginTop: '0.15rem' }}>
                  Break: {p2Break}
                </div>
              )}
              {p2HighBreak > 0 && (
                <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.15rem' }}>
                  High break: {p2HighBreak}
                </div>
              )}
              {p2AvgShotTime > 0 && (
                <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>
                  Avg shot: {Math.round(p2AvgShotTime)}s
                </div>
              )}
            </div>
          </div>

          {/* Shot clock bar */}
          {!isStandard && !breakOffPending && !clockPaused && (
            <div style={{ marginTop: '1.5rem', maxWidth: '700px', margin: '1.5rem auto 0' }}>
              <div style={{ height: '12px', background: 'rgba(255,255,255,0.08)', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '6px',
                  width: `${Math.max(0, (shotLeft / shotLimit) * 100)}%`,
                  background: shotOver ? '#e74c3c' : shotWarn ? '#e67e22' : '#2ecc71',
                  transition: 'width 0.25s linear, background 0.3s',
                }} />
              </div>
              <div style={{ textAlign: 'center', fontSize: '1rem', color: shotOver ? '#e74c3c' : shotWarn ? '#e67e22' : 'var(--chalk-dim)', marginTop: '0.5rem', fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>
                {t.shotClock} {shotLeft}s {inSecondHalf && !isSnooker900 ? `(${t.secondHalf})` : ''}
              </div>
            </div>
          )}

          {breakOffPending && (
            <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '1.1rem', color: 'var(--chalk-dim)' }}>
              ⏳ {t.whoBreaks}
            </div>
          )}
        </div>

        {/* ── Shot history — full width below ── */}
        {ls && ls.shots.length > 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '16px', padding: '1rem 1.5rem',
          }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.6rem' }}>
              {t.shotHistory} ({ls.shots.length})
            </div>
            {/* Grid of shot rows — 2 columns on wide screens */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '320px', overflowY: 'auto' }}>
              {[...ls.shots].reverse().map((shot, i) => {
                const shooter = shot.playerId === ls.player1Id ? p1 : p2;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.92rem', padding: '0.45rem 0.75rem', background: shot.isFoul ? 'rgba(192,57,43,0.08)' : i === 0 ? 'rgba(201,168,76,0.07)' : 'rgba(255,255,255,0.02)', borderRadius: '6px', border: `1px solid ${shot.isFoul ? 'rgba(192,57,43,0.15)' : i === 0 ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.04)'}` }}>
                    <span style={{ color: 'rgba(255,255,255,0.25)', minWidth: '24px', textAlign: 'right', fontSize: '0.72rem' }}>{ls.shots.length - i}</span>
                    <span style={{ color: 'var(--chalk-dim)', minWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{shooter.name.split(' ')[0]}</span>
                    {shot.ballPotted && <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: BALL_CSS[shot.ballPotted as BallColour], border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }} />}
                    <span style={{ flex: 1, color: shot.isFoul ? '#e74c3c' : shot.points > 0 ? 'var(--gold)' : 'var(--chalk-dim)' }}>
                      {shot.isFoul ? `${t.foul} +${shot.foulPenalty}` : shot.ballPotted ? `${shot.ballPotted} +${shot.points}${shot.isFreeBall ? ` · 🔴 ${t.freeBallLabel}` : ''}` : t.miss}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem', flexShrink: 0 }}>{shot.shotDurationSeconds}s</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <style>{`.widescreen-toggle { display: none !important; } @media (min-width: 900px) { .widescreen-toggle { display: flex !important; } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--page-pad)', maxWidth: '600px', margin: '0 auto' }}>

      {/* ── Complete ── */}
      {complete && winner && (
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🏆</div>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2.5rem', color: 'var(--gold)', lineHeight: 1, marginBottom: '1.5rem' }}>{t.matchComplete}</h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', alignItems: 'center' }}>
            {[
              { player: p1, score: bestOf > 1 ? frameWins1 : (ls?.score1 ?? match.score1 ?? 0), highBreak: bestOf > 1 ? p1MatchHighBreak : p1HighBreak, avgShotTime: p1AvgShotTime },
              { player: p2, score: bestOf > 1 ? frameWins2 : (ls?.score2 ?? match.score2 ?? 0), highBreak: bestOf > 1 ? p2MatchHighBreak : p2HighBreak, avgShotTime: p2AvgShotTime },
            ].map(({ player, score, highBreak, avgShotTime }, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <Av player={player} size={90} active={winner._id === String(player._id)} />
                <div style={{ fontWeight: 700, fontSize: '1rem', marginTop: '0.5rem' }}>{player.name}</div>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: '4rem', color: winner._id === String(player._id) ? 'var(--gold)' : 'var(--chalk)', lineHeight: 1 }}>{score}</div>
                {winner._id === String(player._id) && <div style={{ color: 'var(--gold)', fontSize: '0.9rem', marginTop: '0.25rem' }}>🏆 {t.wins}</div>}
                {highBreak > 0 && <div style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', marginTop: '0.25rem' }}>High break: {highBreak}</div>}
                {avgShotTime > 0 && <div style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', marginTop: '0.15rem' }}>Avg shot: {Math.round(avgShotTime)}s</div>}
              </div>
            ))}
          </div>
          <FrameBreakdownList frames={frames} p1={p1} p2={p2} />
        </div>
      )}

      {/* ── Not started ── */}
      {!inProgress && !complete && (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--chalk-dim)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
          <p style={{ fontSize: '1rem' }}>{t.notStarted}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1.5rem' }}>
            {[p1, p2].map((p, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <Av player={p} size={70} />
                <div style={{ fontWeight: 600, marginTop: '0.4rem', fontSize: '0.9rem' }}>{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── In progress ── */}
      {inProgress && (
        <>
          {/* Match Paused overlay */}
          {clockPaused && (
            <div style={{
              background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
              borderRadius: '14px', padding: '2rem 1rem',
              textAlign: 'center', marginBottom: '1rem',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.4rem' }}>⏸</div>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: '2rem', color: 'var(--chalk)', letterSpacing: '0.08em', lineHeight: 1 }}>
                Match Paused
              </div>
            </div>
          )}
          {/* Score card — large */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '18px',
            padding: '1.5rem', marginBottom: '1rem',
          }}>
            <FrameProgressBar bestOf={bestOf} framesToWin={framesToWin} frameWins1={frameWins1} frameWins2={frameWins2} currentFrame={currentFrame} complete={complete} p1={p1} p2={p2} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
              {/* Player 1 */}
              <div style={{ flex: 1, textAlign: 'center' }}>
                <Av player={p1} size={80} active={!breakOffPending && curPlayer?._id === p1._id} />
                <div style={{ fontWeight: 600, fontSize: '0.95rem', marginTop: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p1.name}</div>
                {!breakOffPending && curPlayer?._id === p1._id && <div style={{ fontSize: '0.62rem', color: '#2ecc71', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '0.15rem' }}>▶ {t.atTheTable}</div>}
                <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(3.5rem,12vw,5rem)', lineHeight: 1, color: !breakOffPending && curPlayer?._id === p1._id ? 'var(--gold)' : 'var(--chalk)', marginTop: '0.25rem' }}>{ls?.score1 ?? 0}</div>
                {p1Break > 0 && <div style={{ fontSize: '0.65rem', color: 'var(--gold)', fontFamily: 'Bebas Neue', letterSpacing: '0.06em', marginTop: '0.1rem' }}>Break: {p1Break}</div>}
                {p1HighBreak > 0 && <div style={{ fontSize: '0.58rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>High break: {p1HighBreak}</div>}
                {p1AvgShotTime > 0 && <div style={{ fontSize: '0.58rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>Avg shot: {Math.round(p1AvgShotTime)}s</div>}
              </div>

              {/* Centre */}
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--chalk-dim)' }}>{t.vsLabel}</div>
                {/* Match clock */}
                {!isStandard && !clockPaused && (
                  <div style={{
                    fontFamily: 'Bebas Neue', fontSize: 'clamp(1.1rem,4vw,1.5rem)',
                    color: remaining <= 60 ? '#e74c3c' : remaining <= 120 ? '#e67e22' : 'var(--chalk-dim)',
                    letterSpacing: '0.04em',
                  }}>{timeFmt}</div>
                )}
                {!isStandard && clockPaused && <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)' }}>⏸</div>}
                {halfTime > 0 && (
                  <div style={{ fontSize: '0.62rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '0.1rem' }}>
                    {inSecondHalf ? t.secondHalf : t.firstHalf}
                  </div>
                )}
                {/* Reds remaining */}
                {ls && ls.reds_remaining > 0 && (
                  <div style={{ marginTop: '0.35rem', fontSize: '0.65rem', color: 'var(--chalk-dim)' }}>
                    {ls.reds_remaining} {t.redsRemaining}
                  </div>
                )}
              </div>

              {/* Player 2 */}
              <div style={{ flex: 1, textAlign: 'center' }}>
                <Av player={p2} size={80} active={!breakOffPending && curPlayer?._id === p2._id} />
                <div style={{ fontWeight: 600, fontSize: '0.95rem', marginTop: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p2.name}</div>
                {!breakOffPending && curPlayer?._id === p2._id && <div style={{ fontSize: '0.62rem', color: '#2ecc71', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '0.15rem' }}>▶ {t.atTheTable}</div>}
                <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(3.5rem,12vw,5rem)', lineHeight: 1, color: !breakOffPending && curPlayer?._id === p2._id ? 'var(--gold)' : 'var(--chalk)', marginTop: '0.25rem' }}>{ls?.score2 ?? 0}</div>
                {p2Break > 0 && <div style={{ fontSize: '0.65rem', color: 'var(--gold)', fontFamily: 'Bebas Neue', letterSpacing: '0.06em', marginTop: '0.1rem' }}>Break: {p2Break}</div>}
                {p2HighBreak > 0 && <div style={{ fontSize: '0.58rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>High break: {p2HighBreak}</div>}
                {p2AvgShotTime > 0 && <div style={{ fontSize: '0.58rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>Avg shot: {Math.round(p2AvgShotTime)}s</div>}
              </div>
            </div>

            {/* Shot clock bar */}
            {!isStandard && !breakOffPending && !clockPaused && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '3px',
                    width: `${Math.max(0, (shotLeft / shotLimit) * 100)}%`,
                    background: shotOver ? '#e74c3c' : shotWarn ? '#e67e22' : '#2ecc71',
                    transition: 'width 0.25s linear, background 0.3s',
                  }} />
                </div>
                <div style={{ textAlign: 'center', fontSize: '0.7rem', color: shotOver ? '#e74c3c' : shotWarn ? '#e67e22' : 'var(--chalk-dim)', marginTop: '0.3rem', fontFamily: 'Bebas Neue', letterSpacing: '0.04em' }}>
                  {t.shotClock} {shotLeft}s {inSecondHalf && !isSnooker900 ? `(${t.secondHalf})` : ''}
                </div>
              </div>
            )}

            {/* Break-off pending */}
            {breakOffPending && (
              <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--chalk-dim)' }}>
                ⏳ {t.whoBreaks}
              </div>
            )}
          </div>

          {/* Shot history */}
          {ls && ls.shots.length > 0 && (
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: '12px', padding: '0.85rem',
            }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
                {t.shotHistory} ({ls.shots.length})
              </div>
              <div style={{ maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {[...ls.shots].reverse().map((shot, i) => {
                  const shooter = shot.playerId === ls.player1Id ? p1 : p2;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', padding: '0.35rem 0.6rem', background: shot.isFoul ? 'rgba(192,57,43,0.08)' : i === 0 ? 'rgba(201,168,76,0.07)' : 'rgba(255,255,255,0.02)', borderRadius: '5px', border: `1px solid ${shot.isFoul ? 'rgba(192,57,43,0.15)' : i === 0 ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.04)'}` }}>
                      <span style={{ color: 'rgba(255,255,255,0.2)', minWidth: '20px', textAlign: 'right', fontSize: '0.68rem' }}>{ls.shots.length - i}</span>
                      <span style={{ color: 'var(--chalk-dim)', minWidth: '54px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{shooter.name.split(' ')[0]}</span>
                      {shot.ballPotted && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: BALL_CSS[shot.ballPotted as BallColour], border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }} />}
                      <span style={{ flex: 1, color: shot.isFoul ? '#e74c3c' : shot.points > 0 ? 'var(--gold)' : 'var(--chalk-dim)' }}>
                        {shot.isFoul ? `${t.foul} +${shot.foulPenalty}` : shot.ballPotted ? `${shot.ballPotted} +${shot.points}${shot.isFreeBall ? ` · 🔴 ${t.freeBallLabel}` : ''}` : t.miss}
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.68rem', flexShrink: 0 }}>{shot.shotDurationSeconds}s</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
